import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';

import * as faceapi from 'face-api.js';
import { FaceMesh } from '@mediapipe/face_mesh';
import { Camera } from '@mediapipe/camera_utils';

import { useFRcamera } from '../../useAiCamera';
import { settings } from 'nprogress';

const FaceMatcherContext = createContext({
  webcamRef: null,
  canvasRef: null,
  loadingModels: false,
  faceDetected: {
    face: false,
    details: { confidence: 0, name: '' }
  },
  allowCapture: false,
  capturedImage: null,
  capture: () => {},
  bioID: '',
  handleSetBioID: () => {}
});

function FaceMatcherProvider({ children }) {
  const { dtImg, processFace, settings, selected } = useFRcamera();
  const { landmark = false, box = false, value = {} } = settings;

  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const faceMeshRef = useRef(null);
  const cameraRef = useRef(null);

  const faceMatcherRef = useRef(null);
  const modelsReadyRef = useRef(false);

  const processingBioRef = useRef(false);

  const [loadingModels, setLoadingModels] = useState(false);

  const [faceDetected, setFaceDetected] = useState({
    face: false,
    details: {
      confidence: 0,
      name: ''
    }
  });

  const [allowCapture, setAllowCapture] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [bioID, setBioID] = useState('');

  // =========================
  // LOAD MODELS
  // =========================
  const loadModels = async () => {
    try {
      setLoadingModels(true);

      const MODEL_URL = `${window.location.origin}/models`;

      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
      ]);

      modelsReadyRef.current = true;
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingModels(false);
    }
  };

  // =========================
  // LOAD IMAGES
  // =========================
  const loadLabeledImages = async selectedLabel => {
    const id = value?.id || dtImg?.value?.id;
    const images = value?.images || dtImg?.value?.images || [];

    if (selectedLabel !== id) return [];

    const loadImage = src =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

    const descriptions = [];

    for (let i = 0; i < images.length; i++) {
      try {
        const img = await loadImage(images[i]);

        const detection = await faceapi
          .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (detection) descriptions.push(detection.descriptor);
      } catch (err) {
        console.error(err);
      }
    }

    if (!descriptions.length) return [];

    return [new faceapi.LabeledFaceDescriptors(selectedLabel, descriptions)];
  };

  // =========================
  // SET BIO ID (SAFE)
  // =========================
  const handleSetBioID = async id => {
    setBioID(id);

    faceMatcherRef.current = null;
    setAllowCapture(false);

    setFaceDetected({
      face: false,
      details: { confidence: 0, name: '' }
    });

    if (!modelsReadyRef.current || !id) return;

    processingBioRef.current = true;

    try {
      const labeledDescriptors = await loadLabeledImages(id);

      if (!labeledDescriptors.length) {
        faceMatcherRef.current = null;
        return;
      }

      faceMatcherRef.current = new faceapi.FaceMatcher(labeledDescriptors, 0.6);
    } catch (err) {
      console.error(err);
      faceMatcherRef.current = null;
    } finally {
      processingBioRef.current = false;
    }
  };

  // =========================
  // CLEAR CANVAS
  // =========================
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // =========================
  // DRAW RESULT
  // =========================
  const drawResult = (video, detection, label) => {
    const canvas = canvasRef.current;
    if (!canvas || !detection) return;

    const ctx = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const box = detection.detection.box;

    ctx.strokeStyle = label === 'Unknown' ? '#ff4444' : '#00ff00';
    ctx.lineWidth = 2;

    ctx.strokeRect(box.x, box.y, box.width, box.height);

    ctx.fillStyle = ctx.strokeStyle;
    ctx.font = '18px Arial';

    ctx.fillText(label, box.x, box.y - 10);
  };

  const drawLandmarks = results => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // original resolution
    canvas.width = results.image.width;
    canvas.height = results.image.height;

    // STRETCH DISPLAY HEIGHT
    canvas.style.width = '100%';
    canvas.style.height = '102%'; // stretch vertically

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!results.multiFaceLandmarks) return;

    ctx.fillStyle = '#ffffff';

    for (const landmarks of results.multiFaceLandmarks) {
      landmarks.forEach(p => {
        ctx.beginPath();

        ctx.arc(p.x * canvas.width, p.y * canvas.height, 1, 0, 2 * Math.PI);

        ctx.fill();
      });
    }
  };
  // =========================
  // RECOGNITION
  // =========================
  const recognizeFace = async video => {
    if (processingBioRef.current) return null;
    if (!faceMatcherRef.current) return null;

    const detection = await faceapi
      .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
      .withFaceLandmarks()
      .withFaceDescriptor();

    if (!detection) {
      setAllowCapture(false);
      return null;
    }

    if (!faceMatcherRef.current) return null;

    const bestMatch = faceMatcherRef.current.findBestMatch(
      detection.descriptor
    );

    const confidence = Math.max(0, (1 - bestMatch.distance) * 100);

    let label = bestMatch.label;

    if (confidence < 55 || label === 'unknown') {
      label = 'Unknown';
    }

    setAllowCapture(label !== 'Unknown');

    return { detection, label };
  };

  // =========================
  // MEDIAPIPE INIT
  // =========================
  const initFaceMesh = () => {
    const video = webcamRef.current?.video;
    if (!video) return;

    const faceMesh = new FaceMesh({
      locateFile: file =>
        `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
    });

    faceMesh.setOptions({
      maxNumFaces: 1,
      refineLandmarks: true,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5
    });

    faceMesh.onResults(async results => {
      if (!results.multiFaceLandmarks?.length) {
        setAllowCapture(false);
        clearCanvas();

        setFaceDetected({
          face: false,
          details: { confidence: 0, name: '' }
        });

        return;
      }

      const result = await recognizeFace(video);

      if (landmark) {
        drawLandmarks(results);
      }

      if (!result) return;

      const { detection, label } = result;

      const isMatched = label && label !== 'Unknown';

      setFaceDetected({
        face: true,
        details: {
          confidence: 0,
          name: label
        }
      });

      clearCanvas();

      if (isMatched) {
        //MATCHED: show box + label ONLY
        drawResult(video, detection, label);
        // drawLandmarks(results);

        setAllowCapture(true);
      } else {
        //NOT MATCHED: show landmarks ONLY
        if (landmark) {
          drawLandmarks(results);
        }
        setAllowCapture(false);
      }
    });

    faceMeshRef.current = faceMesh;

    const camera = new Camera(video, {
      onFrame: async () => {
        await faceMesh.send({ image: video });
      },
      width: 640,
      height: 480
    });

    camera.start();
    cameraRef.current = camera;
  };

  // =========================
  // CAPTURE
  // =========================
  const capture = useCallback(() => {
    if (!allowCapture) return;

    const image = webcamRef.current?.getScreenshot();
    if (!image) return;

    processFace(image);
    setCapturedImage(image);
  }, [allowCapture]);

  // =========================
  // INIT
  // =========================
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      await loadModels();

      if (!mounted) return;

      initFaceMesh();
    };
     init();

    return () => {
      mounted = false;

      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, []);

  return (
    <FaceMatcherContext.Provider
      value={{
        webcamRef,
        canvasRef,
        loadingModels,
        faceDetected,
        allowCapture,
        capturedImage,
        capture,
        bioID,
        handleSetBioID
      }}
    >
      {children}
    </FaceMatcherContext.Provider>
  );
}

export const useFaceMatcher = () => {
  const ctx = useContext(FaceMatcherContext);

  if (!ctx) {
    throw new Error('useFaceMatcher must be used within FaceMatcherProvider');
  }

  return ctx;
};

export default FaceMatcherProvider;
